import React, { useState, useMemo, useEffect } from 'react';
import SlideoutPanel from '../components/Common/SlideoutPanel';
import { useSlideout } from '../hooks/useSlideout';
import PageHeader from '../components/Common/PageHeader';
import { TableSkeleton, CardSkeleton, StatsSkeleton } from '../components/Common/SkeletonLoader';
import apiService from '../services/api';

const ServicesView = ({ showNotification }) => {
  const [viewMode, setViewMode] = useState('table');
  const [searchTerm, setSearchTerm] = useState('');
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [apiError, setApiError] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [filters, setFilters] = useState({
    category: '',
    status: '',
    practitionerType: '',
    priceRange: '',
    duration: ''
  });

  // Use shared slideout hook
  const { isOpen, title, content, width, openSlideout, closeSlideout } = useSlideout();

  // Fetch services from API
  useEffect(() => {
    fetchServices();
  }, []);

  const fetchServices = async () => {
    try {
      setLoading(true);
      setApiError(false);
      const response = await apiService.getServices();
      const transformedServices = response.map(transformApiService);
      setServices(transformedServices);
    } catch (error) {
      setServices([]);
      setApiError(true);
    } finally {
      setLoading(false);
    }
  };

  // Transform API service data to match frontend structure
  const transformApiService = (apiService) => ({
    id: apiService.id,
    name: apiService.name || 'Unknown Service',
    description: apiService.description || '',
    category: apiService.category || 'General',
    shortDescription: apiService.shortDescription || '',
    status: apiService.status === 0 ? 'active' : 'inactive',
    pricing: {
      basePrice: apiService.basePrice || 0,
      currency: 'GBP'
    },
    scheduling: {
      duration: apiService.durationMinutes || 60,
      minDuration: apiService.minDurationMinutes,
      maxDuration: apiService.maxDurationMinutes,
      bufferTime: apiService.bufferTimeMinutes || 0
    },
    requirements: {
      practitionerQualifications: [],
      equipment: [],
      roomType: ''
    },
    booking: {
      onlineBookingEnabled: apiService.onlineBookingEnabled,
      requiresApproval: apiService.requiresApproval,
      allowWalkIn: apiService.allowWalkIn,
      minimumNotice: apiService.minimumNoticeHours || 24,
      maxAdvanceBooking: apiService.maxAdvanceBookingDays || 90
    },
    appearance: {
      color: apiService.color || '#4CAF50',
      icon: apiService.icon || '🏥'
    },
    performance: {
      totalBookings: 0,
      totalRevenue: 0,
      averageRating: 0,
      popularityScore: 0
    }
  });

  // Calculate stats
  const stats = useMemo(() => {
    const activeServices = services.filter(s => s.status === 'active');
    const totalRevenue = services.reduce((sum, s) => sum + (s.performance?.totalRevenue || 0), 0);
    const averagePrice = services.length > 0 
      ? services.reduce((sum, s) => sum + (s.pricing?.basePrice || 0), 0) / services.length 
      : 0;

    return {
      totalServices: services.length,
      activeServices: activeServices.length,
      averagePrice: averagePrice,
      totalRevenue: totalRevenue
    };
  }, [services]);

  // Filter and search services
  const filteredServices = useMemo(() => {
    return services.filter(service => {
      const matchesSearch = service.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           service.category.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           service.description.toLowerCase().includes(searchTerm.toLowerCase());
      
      const matchesCategory = !filters.category || service.category === filters.category;
      const matchesStatus = !filters.status || service.status === filters.status;
      const matchesPractitioner = !filters.practitionerType || 
                                 service.requirements?.practitionerQualifications?.includes(filters.practitionerType);
      
      const matchesPriceRange = !filters.priceRange || (() => {
        const price = service.pricing?.basePrice || 0;
        switch(filters.priceRange) {
          case 'under-50': return price < 50;
          case '50-100': return price >= 50 && price <= 100;
          case 'over-100': return price > 100;
          default: return true;
        }
      })();

      const matchesDuration = !filters.duration || (() => {
        const duration = service.scheduling?.duration || 0;
        switch(filters.duration) {
          case 'short': return duration <= 30;
          case 'medium': return duration > 30 && duration <= 60;
          case 'long': return duration > 60;
          default: return true;
        }
      })();

      return matchesSearch && matchesCategory && matchesStatus && 
             matchesPractitioner && matchesPriceRange && matchesDuration;
    });
  }, [services, searchTerm, filters]);


  const handleAddService = () => {
    openSlideout(
      'Add New Service',
      <AddServiceForm
        onSubmit={handleCreateService}
        onCancel={closeSlideout}
        submitting={submitting}
      />,
      '750px'
    );
  };

  const handleCreateService = async (serviceData) => {
    if (submitting) return;

    try {
      setSubmitting(true);

      // Transform frontend service data to API format
      const apiData = transformServiceToApi(serviceData);
      const newService = await apiService.createService(apiData);

      // Add to local state
      const transformedService = transformApiService(newService);
      setServices(prev => [...prev, transformedService]);

      showNotification('Service created successfully', 'success');
      closeSlideout();
    } catch (error) {
      console.error('Error creating service:', error);
      showNotification('Failed to create service', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const transformServiceToApi = (serviceData) => ({
    name: serviceData.name,
    description: serviceData.description,
    category: serviceData.category,
    shortDescription: serviceData.shortDescription,
    status: serviceData.status === 'active' ? 0 : 1,
    basePrice: serviceData.pricing?.basePrice || 0,
    durationMinutes: serviceData.scheduling?.duration || 60,
    minDurationMinutes: serviceData.scheduling?.minDuration,
    maxDurationMinutes: serviceData.scheduling?.maxDuration,
    bufferTimeMinutes: serviceData.scheduling?.bufferTime || 0,
    color: serviceData.appearance?.color,
    icon: serviceData.appearance?.icon,
    onlineBookingEnabled: serviceData.booking?.onlineBookingEnabled ?? true,
    requiresApproval: serviceData.booking?.requiresApproval ?? false,
    allowWalkIn: serviceData.booking?.allowWalkIn ?? false,
    minimumNoticeHours: serviceData.booking?.minimumNotice || 24,
    maxAdvanceBookingDays: serviceData.booking?.maxAdvanceBooking || 90
  });

  const handleEditService = (service) => {
    openSlideout(
      'Edit Service',
      <AddServiceForm
        service={service}
        isEditing={true}
        onSubmit={(serviceData) => handleUpdateService(service.id, serviceData)}
        onCancel={closeSlideout}
        submitting={submitting}
      />,
      '750px'
    );
  };

  const handleUpdateService = async (serviceId, serviceData) => {
    if (submitting) return;

    try {
      setSubmitting(true);

      // Transform frontend service data to API format
      const apiData = transformServiceToApi(serviceData);
      const updatedService = await apiService.updateService(serviceId, apiData);

      // Update local state
      const transformedService = transformApiService(updatedService);
      setServices(prev => prev.map(service =>
        service.id === serviceId ? transformedService : service
      ));

      showNotification('Service updated successfully', 'success');
      closeSlideout();
    } catch (error) {
      console.error('Error updating service:', error);
      showNotification('Failed to update service', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const handleViewService = (service) => {
    openSlideout(
      'Service Details',
      <ServiceDetail service={service} />,
      '600px'
    );
  };

  const handleDeleteService = async (serviceId) => {
    if (window.confirm('Are you sure you want to delete this service?')) {
      try {
        await apiService.deleteService(serviceId);
        setServices(prev => prev.filter(service => service.id !== serviceId));
        showNotification('Service deleted successfully', 'success');
      } catch (error) {
        console.error('Error deleting service:', error);
        showNotification('Failed to delete service', 'error');
      }
    }
  };

  const handleExport = () => {
    showNotification('Services exported to CSV', 'success');
  };

  return (
    <div className="services-view">
      {/* Page Header */}
      <PageHeader pageKey="services">
        <button className="btn btn-secondary" onClick={handleExport}>
          <span className="icon icon--export icon--default"></span>
          Export
        </button>
        <button className="btn btn-primary" onClick={handleAddService}>
          <span className="icon icon--add icon--success"></span>
          Add Service
        </button>
      </PageHeader>

      {/* Stats Cards */}
      {loading ? (
        <StatsSkeleton count={3} />
      ) : (
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-content">
              <div className="stat-number">{stats.totalServices}</div>
              <div className="stat-label">Total Services</div>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-content">
              <div className="stat-number">{stats.activeServices}</div>
              <div className="stat-label">Active Services</div>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-content">
              <div className="stat-number">£{stats.totalRevenue.toLocaleString()}</div>
              <div className="stat-label">Total Revenue</div>
            </div>
          </div>
        </div>
      )}

      {/* Filters and Search */}
      <div className="filters-section">
        <div className="search-bar">
          <input
            type="text"
            placeholder="Search services..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>
        
        <div className="filters-bar">
          <select
            value={filters.category}
            onChange={(e) => setFilters(prev => ({ ...prev, category: e.target.value }))}
            className="filter-select"
          >
            <option value="">All Categories</option>
            {[...new Set(services.map(s => s.category).filter(Boolean))].sort().map(cat => (
              <option key={cat} value={cat}>{cat}</option>
            ))}
          </select>

          <select
            value={filters.status}
            onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
            className="filter-select"
          >
            <option value="">All Statuses</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
            <option value="draft">Draft</option>
          </select>

          <select
            value={filters.priceRange}
            onChange={(e) => setFilters(prev => ({ ...prev, priceRange: e.target.value }))}
            className="filter-select"
          >
            <option value="">All Prices</option>
            <option value="under-50">Under £50</option>
            <option value="50-100">£50 - £100</option>
            <option value="over-100">Over £100</option>
          </select>

          <select
            value={filters.duration}
            onChange={(e) => setFilters(prev => ({ ...prev, duration: e.target.value }))}
            className="filter-select"
          >
            <option value="">All Durations</option>
            <option value="short">≤ 30 min</option>
            <option value="medium">31-60 min</option>
            <option value="long">> 60 min</option>
          </select>

          <div className="view-toggle">
            <button
              className={`view-btn ${viewMode === 'table' ? 'active' : ''}`}
              onClick={() => setViewMode('table')}
            >
              📋 Table
            </button>
            <button
              className={`view-btn ${viewMode === 'cards' ? 'active' : ''}`}
              onClick={() => setViewMode('cards')}
            >
              🎴 Cards
            </button>
          </div>
        </div>
      </div>

      {/* Content Area */}
      <div className="content-card">
        {loading ? (
          viewMode === 'table'
            ? <TableSkeleton rows={6} columns={7} />
            : <CardSkeleton cards={6} />
        ) : apiError ? (
          <div className="api-error-state">
            <div className="api-error-state__icon">⚠️</div>
            <h3>Could not load services</h3>
            <p>There was a problem connecting to the server. Please check your connection and try again.</p>
            <button className="btn btn-primary" onClick={fetchServices}>Retry</button>
          </div>
        ) : viewMode === 'table' ? (
          <ServicesTable
            services={filteredServices}
            onEdit={handleEditService}
            onView={handleViewService}
            onDelete={handleDeleteService}
          />
        ) : (
          <ServicesCards
            services={filteredServices}
            onEdit={handleEditService}
            onView={handleViewService}
            onDelete={handleDeleteService}
          />
        )}
      </div>

      <SlideoutPanel
        isOpen={isOpen}
        onClose={closeSlideout}
        title={title}
        width={width}
      >
        {content}
      </SlideoutPanel>
    </div>
  );
};

// Services Table Component
const ServicesTable = ({ services, onEdit, onView, onDelete }) => {
  return (
    <div className="material-table-container">
      <div className="material-table-header">
        <h3 className="material-table-title">Services Overview</h3>
        <span className="material-table-meta">Showing {services.length} services</span>
      </div>

      <table className="material-table">
        <thead>
          <tr>
            <th className="material-th">Service Name</th>
            <th className="material-th">Category</th>
            <th className="material-th">Duration</th>
            <th className="material-th">Price</th>
            <th className="material-th">Practitioners</th>
            <th className="material-th">Status</th>
            <th className="material-th">Actions</th>
          </tr>
        </thead>
        <tbody>
          {services.map(service => (
            <tr key={service.id} className="material-tr">
              <td className="material-cell">
                <div className="material-service-cell">
                  <div className="material-service-info">
                    <span className="material-body-large">{service.name}</span>
                    <span className="material-body-small material-text-secondary">{service.subcategory}</span>
                  </div>
                </div>
              </td>
              <td className="material-cell">
                <span className="material-body-medium">{service.category || 'N/A'}</span>
              </td>
              <td className="material-cell">
                <span className="material-body-medium">{service.scheduling?.duration || 0} min</span>
              </td>
              <td className="material-cell">
                <span className="material-body-medium">{service.pricing?.basePrice > 0 ? `£${service.pricing.basePrice}` : 'Free'}</span>
              </td>
              <td className="material-cell">
                <span className="material-body-medium">
                  {service.availability?.practitionersCount || 0} practitioners
                </span>
              </td>
              <td className="material-cell">
                <span className={`material-chip--${service.status === 'active' ? 'success' : service.status === 'inactive' ? 'error' : 'warning'}`}>
                  {service.status}
                </span>
              </td>
              <td className="actions-cell">
                <div className="action-icons">
                  <button
                    className="action-icon action-icon--view"
                    onClick={() => onView(service)}
                    data-tooltip="View Details"
                  ></button>
                  <button
                    className="action-icon action-icon--edit"
                    onClick={() => onEdit(service)}
                    data-tooltip="Edit Service"
                  ></button>
                  <button
                    className="action-icon action-icon--delete"
                    onClick={() => onDelete(service.id)}
                    data-tooltip="Delete Service"
                  ></button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {services.length === 0 && (
        <div className="empty-state">
          <p>No services found matching your criteria.</p>
        </div>
      )}
    </div>
  );
};

// Services Cards Component
const ServicesCards = ({ services = [], onEdit, onView, onDelete }) => {
  return (
    <div className="services-grid">
      {services.map(service => (
        <div key={service.id} className="service-card">
          <div className="service-card-header">
            <div className="service-info">
              <h3 className="service-name">{service.name}</h3>
              <p className="service-category">{service.category} • {service.subcategory}</p>
            </div>
            <span className={`status-badge status-${service.status}`}>
              {service.status}
            </span>
          </div>
          
          <div className="service-card-content">
            <p className="service-description">{service.shortDescription}</p>
            
            <div className="service-details">
              <div className="detail-item">
                <span className="detail-label">Duration:</span>
                <span className="detail-value">{service.scheduling?.duration || 0} min</span>
              </div>
              <div className="detail-item">
                <span className="detail-label">Price:</span>
                <span className="detail-value">{service.pricing?.basePrice > 0 ? `£${service.pricing.basePrice}` : 'Free'}</span>
              </div>
              <div className="detail-item">
                <span className="detail-label">Practitioners:</span>
                <span className="detail-value">{service.availability?.practitionersCount || 0}</span>
              </div>
            </div>
          </div>
          
          <div className="service-card-actions">
            <button 
              className="btn btn-secondary"
              onClick={() => onView(service)}
            >
              View Details
            </button>
            <button 
              className="btn btn-primary"
              onClick={() => onEdit(service)}
            >
              Edit
            </button>
            <button 
              className="btn-icon"
              onClick={() => onDelete(service.id)}
              title="Delete"
            >
              🗑️
            </button>
          </div>
        </div>
      ))}
      {services.length === 0 && (
        <div className="empty-state">
          <p>No services found matching your criteria.</p>
        </div>
      )}
    </div>
  );
};

// Add/Edit Service Form Component
const AddServiceForm = ({ service, onSubmit, onCancel, isEditing = false }) => {
  const [formData, setFormData] = useState({
    name: service?.name || '',
    category: service?.category || '',
    subcategory: service?.subcategory || '',
    description: service?.description || '',
    shortDescription: service?.shortDescription || '',
    pricing: {
      basePrice: service?.pricing?.basePrice || 0,
      memberPrice: service?.pricing?.memberPrice || 0,
      currency: service?.pricing?.currency || 'GBP'
    },
    scheduling: {
      duration: service?.scheduling?.duration || 60,
      bufferTime: service?.scheduling?.bufferTime || 15,
      maxSessionsPerDay: service?.scheduling?.maxSessionsPerDay || 8
    },
    requirements: {
      practitionerQualifications: service?.requirements?.practitionerQualifications || [],
      equipmentNeeded: service?.requirements?.equipmentNeeded || [],
      roomType: service?.requirements?.roomType || ''
    },
    availability: {
      locations: service?.availability?.locations || [],
      practitionersCount: service?.availability?.practitionersCount || 0
    },
    businessDetails: {
      costToProvide: service?.businessDetails?.costToProvide || 0,
      profitMargin: service?.businessDetails?.profitMargin || 0
    },
    bookingRules: {
      advanceBookingTime: service?.bookingRules?.advanceBookingTime || 24,
      cancellationPolicy: service?.bookingRules?.cancellationPolicy || '24 hours notice required',
      rebookingAllowed: service?.bookingRules?.rebookingAllowed || true
    },
    medicalInfo: {
      contraindications: service?.medicalInfo?.contraindications || [],
      ageRestrictions: service?.medicalInfo?.ageRestrictions || '',
      healthScreeningRequired: service?.medicalInfo?.healthScreeningRequired || false
    },
    marketing: {
      featured: service?.marketing?.featured || false,
      onlineBookingEnabled: service?.marketing?.onlineBookingEnabled || true,
      promotionalDescription: service?.marketing?.promotionalDescription || ''
    },
    status: service?.status || 'active'
  });

  const [errors, setErrors] = useState({});

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) newErrors.name = 'Service name is required';
    // category is optional
    if (formData.pricing.basePrice < 0) newErrors.basePrice = 'Base price cannot be negative';
    if (formData.scheduling.duration <= 0) newErrors.duration = 'Duration must be greater than 0';
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validateForm()) {
      onSubmit(formData);
    }
  };

  const updateField = (path, value) => {
    setFormData(prev => {
      const newData = { ...prev };
      const keys = path.split('.');
      let current = newData;
      
      for (let i = 0; i < keys.length - 1; i++) {
        current = current[keys[i]];
      }
      
      current[keys[keys.length - 1]] = value;
      return newData;
    });
  };

  return (
    <form onSubmit={handleSubmit} className="service-form">
      {/* Basic Information */}
      <div className="form-section">
        <h4 className="form-section-title">Basic Information</h4>
        <div className="form-grid">
          <div className="form-group">
            <label className="form-label">Service Name *</label>
            <input
              type="text"
              className={`form-input ${errors.name ? 'error' : ''}`}
              value={formData.name}
              onChange={(e) => updateField('name', e.target.value)}
              placeholder="e.g., Sports Physiotherapy"
            />
            {errors.name && <span className="form-error">{errors.name}</span>}
          </div>

          <div className="form-group">
            <label className="form-label">Category</label>
            <input
              type="text"
              className="form-input"
              value={formData.category}
              onChange={(e) => updateField('category', e.target.value)}
              placeholder="e.g. Physiotherapy, Wellness (optional)"
            />
          </div>

          <div className="form-group">
            <label className="form-label">Status</label>
            <select
              className="form-input"
              value={formData.status}
              onChange={(e) => updateField('status', e.target.value)}
            >
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
              <option value="draft">Draft</option>
            </select>
          </div>
        </div>

        <div className="form-group">
          <label className="form-label">Short Description</label>
          <input
            type="text"
            className="form-input"
            value={formData.shortDescription}
            onChange={(e) => updateField('shortDescription', e.target.value)}
            placeholder="Brief description for listings"
            maxLength={100}
          />
        </div>

        <div className="form-group">
          <label className="form-label">Full Description</label>
          <textarea
            className="form-input"
            value={formData.description}
            onChange={(e) => updateField('description', e.target.value)}
            placeholder="Detailed service description"
            rows={4}
          />
        </div>
      </div>

      {/* Pricing */}
      <div className="form-section">
        <h4 className="form-section-title">Pricing</h4>
        <div className="form-grid">
          <div className="form-group">
            <label className="form-label">Base Price (£) — enter 0 for free</label>
            <input
              type="number"
              className={`form-input ${errors.basePrice ? 'error' : ''}`}
              value={formData.pricing.basePrice}
              onChange={(e) => updateField('pricing.basePrice', parseFloat(e.target.value) || 0)}
              min="0"
              step="0.01"
            />
            {errors.basePrice && <span className="form-error">{errors.basePrice}</span>}
          </div>

          <div className="form-group">
            <label className="form-label">Member Price (£)</label>
            <input
              type="number"
              className="form-input"
              value={formData.pricing.memberPrice}
              onChange={(e) => updateField('pricing.memberPrice', parseFloat(e.target.value) || 0)}
              min="0"
              step="0.01"
            />
          </div>

          <div className="form-group">
            <label className="form-label">Cost to Provide (£)</label>
            <input
              type="number"
              className="form-input"
              value={formData.businessDetails.costToProvide}
              onChange={(e) => updateField('businessDetails.costToProvide', parseFloat(e.target.value) || 0)}
              min="0"
              step="0.01"
            />
          </div>

          <div className="form-group">
            <label className="form-label">Profit Margin (%)</label>
            <input
              type="number"
              className="form-input"
              value={formData.businessDetails.profitMargin}
              onChange={(e) => updateField('businessDetails.profitMargin', parseFloat(e.target.value) || 0)}
              min="0"
              max="100"
              step="0.1"
            />
          </div>
        </div>
      </div>

      {/* Scheduling */}
      <div className="form-section">
        <h4 className="form-section-title">Scheduling</h4>
        <div className="form-grid">
          <div className="form-group">
            <label className="form-label">Duration (minutes) *</label>
            <input
              type="number"
              className={`form-input ${errors.duration ? 'error' : ''}`}
              value={formData.scheduling.duration}
              onChange={(e) => updateField('scheduling.duration', parseInt(e.target.value) || 0)}
              min="15"
              step="15"
            />
            {errors.duration && <span className="form-error">{errors.duration}</span>}
          </div>

          <div className="form-group">
            <label className="form-label">Buffer Time (minutes)</label>
            <input
              type="number"
              className="form-input"
              value={formData.scheduling.bufferTime}
              onChange={(e) => updateField('scheduling.bufferTime', parseInt(e.target.value) || 0)}
              min="0"
              step="5"
            />
          </div>

          <div className="form-group">
            <label className="form-label">Max Sessions Per Day</label>
            <input
              type="number"
              className="form-input"
              value={formData.scheduling.maxSessionsPerDay}
              onChange={(e) => updateField('scheduling.maxSessionsPerDay', parseInt(e.target.value) || 0)}
              min="1"
            />
          </div>

          <div className="form-group">
            <label className="form-label">Advance Booking (hours)</label>
            <input
              type="number"
              className="form-input"
              value={formData.bookingRules.advanceBookingTime}
              onChange={(e) => updateField('bookingRules.advanceBookingTime', parseInt(e.target.value) || 0)}
              min="0"
            />
          </div>
        </div>
      </div>

      {/* Requirements */}
      <div className="form-section">
        <h4 className="form-section-title">Requirements</h4>
        <div className="form-grid">
          <div className="form-group">
            <label className="form-label">Room Type</label>
            <select
              className="form-input"
              value={formData.requirements.roomType}
              onChange={(e) => updateField('requirements.roomType', e.target.value)}
            >
              <option value="">Any Room</option>
              <option value="consultation">Consultation Room</option>
              <option value="treatment">Treatment Room</option>
              <option value="therapy">Therapy Room</option>
              <option value="exercise">Exercise Room</option>
              <option value="specialized">Specialized Room</option>
            </select>
          </div>

          <div className="form-group">
            <label className="form-label">Equipment Needed</label>
            <input
              type="text"
              className="form-input"
              value={formData.requirements.equipmentNeeded.join(', ')}
              onChange={(e) => updateField('requirements.equipmentNeeded', 
                e.target.value.split(',').map(item => item.trim()).filter(Boolean)
              )}
              placeholder="Equipment list (comma separated)"
            />
          </div>

          <div className="form-group">
            <label className="form-label">Age Restrictions</label>
            <input
              type="text"
              className="form-input"
              value={formData.medicalInfo.ageRestrictions}
              onChange={(e) => updateField('medicalInfo.ageRestrictions', e.target.value)}
              placeholder="e.g., 18+, Under 65, All ages"
            />
          </div>
        </div>

        <div className="form-group">
          <label className="form-label">Cancellation Policy</label>
          <textarea
            className="form-input"
            value={formData.bookingRules.cancellationPolicy}
            onChange={(e) => updateField('bookingRules.cancellationPolicy', e.target.value)}
            rows={2}
            placeholder="Describe cancellation terms"
          />
        </div>
      </div>

      {/* Marketing */}
      <div className="form-section">
        <h4 className="form-section-title">Marketing & Booking</h4>
        <div className="form-grid">
          <div className="form-group">
            <label className="form-checkbox">
              <input
                type="checkbox"
                checked={formData.marketing.featured}
                onChange={(e) => updateField('marketing.featured', e.target.checked)}
              />
              Featured Service
            </label>
          </div>

          <div className="form-group">
            <label className="form-checkbox">
              <input
                type="checkbox"
                checked={formData.marketing.onlineBookingEnabled}
                onChange={(e) => updateField('marketing.onlineBookingEnabled', e.target.checked)}
              />
              Enable Online Booking
            </label>
          </div>

          <div className="form-group">
            <label className="form-checkbox">
              <input
                type="checkbox"
                checked={formData.medicalInfo.healthScreeningRequired}
                onChange={(e) => updateField('medicalInfo.healthScreeningRequired', e.target.checked)}
              />
              Health Screening Required
            </label>
          </div>

          <div className="form-group">
            <label className="form-checkbox">
              <input
                type="checkbox"
                checked={formData.bookingRules.rebookingAllowed}
                onChange={(e) => updateField('bookingRules.rebookingAllowed', e.target.checked)}
              />
              Allow Rebooking
            </label>
          </div>
        </div>

        <div className="form-group">
          <label className="form-label">Promotional Description</label>
          <textarea
            className="form-input"
            value={formData.marketing.promotionalDescription}
            onChange={(e) => updateField('marketing.promotionalDescription', e.target.value)}
            rows={3}
            placeholder="Marketing description for promotional materials"
          />
        </div>
      </div>

      {/* Form Actions */}
      <div className="form-actions">
        <button type="button" className="btn btn-secondary" onClick={onCancel}>
          Cancel
        </button>
        <button type="submit" className="btn btn-primary">
          {isEditing ? 'Update Service' : 'Create Service'}
        </button>
      </div>
    </form>
  );
};

// Service Detail View Component
const ServiceDetail = ({ service }) => {
  return (
    <div className="service-detail">
      {/* Header */}
      <div className="service-detail-header">
        <div className="service-title-section">
          <h2 className="service-title">{service.name}</h2>
          <p className="service-category">{service.category} • {service.subcategory}</p>
          <span className={`status-badge status-${service.status}`}>
            {service.status}
          </span>
        </div>
      </div>

      {/* Description */}
      <div className="detail-section">
        <h4 className="detail-section-title">Description</h4>
        <p className="service-description">{service.description}</p>
        {service.marketing.promotionalDescription && (
          <div className="promotional-description">
            <strong>Marketing Description:</strong>
            <p>{service.marketing.promotionalDescription}</p>
          </div>
        )}
      </div>

      {/* Pricing Information */}
      <div className="detail-section">
        <h4 className="detail-section-title">Pricing</h4>
        <div className="pricing-grid">
          <div className="pricing-item">
            <span className="pricing-label">Base Price</span>
            <span className="pricing-value">{service.pricing.basePrice > 0 ? `£${service.pricing.basePrice}` : 'Free'}</span>
          </div>
          {service.pricing.memberPrice > 0 && (
            <div className="pricing-item">
              <span className="pricing-label">Member Price</span>
              <span className="pricing-value">£{service.pricing.memberPrice}</span>
            </div>
          )}
          <div className="pricing-item">
            <span className="pricing-label">Cost to Provide</span>
            <span className="pricing-value">£{service.businessDetails.costToProvide}</span>
          </div>
          <div className="pricing-item">
            <span className="pricing-label">Profit Margin</span>
            <span className="pricing-value">{service.businessDetails.profitMargin}%</span>
          </div>
        </div>
      </div>

      {/* Scheduling Information */}
      <div className="detail-section">
        <h4 className="detail-section-title">Scheduling</h4>
        <div className="scheduling-grid">
          <div className="scheduling-item">
            <span className="scheduling-label">Duration</span>
            <span className="scheduling-value">{service.scheduling.duration} minutes</span>
          </div>
          <div className="scheduling-item">
            <span className="scheduling-label">Buffer Time</span>
            <span className="scheduling-value">{service.scheduling.bufferTime} minutes</span>
          </div>
          <div className="scheduling-item">
            <span className="scheduling-label">Max Sessions/Day</span>
            <span className="scheduling-value">{service.scheduling.maxSessionsPerDay}</span>
          </div>
          <div className="scheduling-item">
            <span className="scheduling-label">Advance Booking</span>
            <span className="scheduling-value">{service.bookingRules.advanceBookingTime} hours</span>
          </div>
        </div>
      </div>

      {/* Requirements */}
      <div className="detail-section">
        <h4 className="detail-section-title">Requirements</h4>
        <div className="requirements-grid">
          <div className="requirement-item">
            <span className="requirement-label">Room Type</span>
            <span className="requirement-value">{service.requirements.roomType || 'Any'}</span>
          </div>
          <div className="requirement-item">
            <span className="requirement-label">Equipment Needed</span>
            <span className="requirement-value">
              {service.requirements.equipmentNeeded.length > 0 
                ? service.requirements.equipmentNeeded.join(', ')
                : 'None specified'
              }
            </span>
          </div>
          <div className="requirement-item">
            <span className="requirement-label">Age Restrictions</span>
            <span className="requirement-value">
              {service.medicalInfo.ageRestrictions || 'All ages'}
            </span>
          </div>
        </div>
      </div>

      {/* Availability */}
      <div className="detail-section">
        <h4 className="detail-section-title">Availability</h4>
        <div className="availability-grid">
          <div className="availability-item">
            <span className="availability-label">Locations</span>
            <span className="availability-value">
              {service.availability.locations.length} locations
            </span>
          </div>
          <div className="availability-item">
            <span className="availability-label">Practitioners</span>
            <span className="availability-value">
              {service.availability.practitionersCount} practitioners
            </span>
          </div>
          <div className="availability-item">
            <span className="availability-label">Online Booking</span>
            <span className="availability-value">
              {service.marketing.onlineBookingEnabled ? 'Enabled' : 'Disabled'}
            </span>
          </div>
          <div className="availability-item">
            <span className="availability-label">Featured Service</span>
            <span className="availability-value">
              {service.marketing.featured ? 'Yes' : 'No'}
            </span>
          </div>
        </div>
      </div>

      {/* Booking Rules */}
      <div className="detail-section">
        <h4 className="detail-section-title">Booking Rules</h4>
        <div className="booking-rules">
          <div className="rule-item">
            <span className="rule-label">Cancellation Policy:</span>
            <span className="rule-value">{service.bookingRules.cancellationPolicy}</span>
          </div>
          <div className="rule-item">
            <span className="rule-label">Rebooking Allowed:</span>
            <span className="rule-value">{service.bookingRules.rebookingAllowed ? 'Yes' : 'No'}</span>
          </div>
          <div className="rule-item">
            <span className="rule-label">Health Screening Required:</span>
            <span className="rule-value">{service.medicalInfo.healthScreeningRequired ? 'Yes' : 'No'}</span>
          </div>
        </div>
      </div>

      {/* Performance Metrics */}
      {service.performance && (
        <div className="detail-section">
          <h4 className="detail-section-title">Performance</h4>
          <div className="performance-grid">
            <div className="performance-item">
              <span className="performance-label">Total Bookings</span>
              <span className="performance-value">{service.performance.totalBookings || 0}</span>
            </div>
            <div className="performance-item">
              <span className="performance-label">Total Revenue</span>
              <span className="performance-value">£{(service.performance.totalRevenue || 0).toLocaleString()}</span>
            </div>
            <div className="performance-item">
              <span className="performance-label">Average Rating</span>
              <span className="performance-value">{service.performance.averageRating || 'N/A'}</span>
            </div>
            <div className="performance-item">
              <span className="performance-label">Popularity Score</span>
              <span className="performance-value">{service.performance.popularityScore || 0}</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ServicesView;